import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { ComboboxSearchField } from '@/components/common/ComboboxSearchField';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { useDebounce } from '@/hooks/useDebounce';
import {
  COMBOBOX_LIST_CLASS,
  COMBOBOX_PANEL_CLASS,
  DROPDOWN_ITEM_CLASS,
} from '@/styles/floatingPanel';
import { isSameComboboxOption } from '@/utils/comboboxOption';
import { getPersonOptionDetail } from '@/utils/personOption';
import { collectorsQueryOptions } from '../api/question.api';
import type { Collector } from '../types/question.types';

interface CollectorSelectProps {
  id: string;
  value: number | undefined;
  onChange: (userId: number | undefined) => void;
}

interface CollectorOption {
  label: string;
  value: number;
  detail: string;
  instituteCount: number;
}

export function CollectorSelect({ id, value, onChange }: CollectorSelectProps) {
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 300);

  const { data, isLoading, isFetching } = useQuery(collectorsQueryOptions(search));

  const items: CollectorOption[] = (data ?? []).map((person: Collector) => ({
    label: person.fullName ?? person.username ?? `#${person.userId}`,
    value: person.userId,
    detail: getPersonOptionDetail(person),
    instituteCount: person.instituteCount,
  }));

  const selected = items.some((item) => item.value === value) ? (value ?? null) : null;

  return (
    <Combobox<number>
      items={items}
      value={selected}
      onValueChange={(next) => onChange(next ?? undefined)}
      isItemEqualToValue={isSameComboboxOption}
      filter={() => true}
    >
      <ComboboxTrigger id={id}>
        <ComboboxValue
          placeholder={isLoading ? LOADING_PLACEHOLDER : 'Search by name, login or HR id'}
        />
        <ComboboxIcon />
      </ComboboxTrigger>

      <ComboboxContent className={COMBOBOX_PANEL_CLASS}>
        <ComboboxSearchField
          placeholder="Name, login, HR id or mobile"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <ComboboxList className={COMBOBOX_LIST_CLASS}>
          {(item: CollectorOption) => (
            <ComboboxItem key={item.value} value={item.value} className={DROPDOWN_ITEM_CLASS}>
              <span className="flex flex-col">
                <span>
                  {item.label}
                  {item.instituteCount === 0 ? (
                    <span className="ml-2 text-xs text-amber-600">covers no institute</span>
                  ) : null}
                </span>
                <span className="text-xs text-muted-foreground">{item.detail}</span>
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxEmpty>
          {isFetching ? 'Searching…' : 'Nobody in your team matches that.'}
        </ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
